import { WritableSignal } from '@angular/core';
import { FormUserData } from '../../lib/liveformseditor/types/form-user-data';
import { DEFAULT_INITIAL_DATE } from '../dates';
import * as formulajs from '@formulajs/formulajs';
import transformers from './index';
import { AbstractControl } from '@angular/forms';

function getSafeValueByType(type: string, stringValue: string, originalValue: any): string {
  if (type === 'date') {
    const isDefaultDate = stringValue === DEFAULT_INITIAL_DATE;
    return stringValue.includes('.')
      ? `DATEVALUE('${isDefaultDate ? '01-01-1000' : stringValue}')`
      : `TIMEVALUE('${isDefaultDate ? '00:00' : stringValue}')`;
  }

  if (type === 'boolean') {
    return originalValue ? 'TRUE()' : 'FALSE()';
  }

  return stringValue.replaceAll('"', '\\"').replaceAll("'", "\\'").replaceAll('\n', '\\n ');
}

function getFormulaValueReady(type: string, originalValue: any): string {
  if (type === 'date') {
    const isDefaultDate = originalValue === DEFAULT_INITIAL_DATE;

    if (originalValue.includes(':')) {
      return `TIMEVALUE('${isDefaultDate ? '00:00' : originalValue}')`;
    } else {
      return `DATEVALUE('${isDefaultDate ? '01-01-1000' : originalValue}')`;
    }
  }

  if (type === 'boolean') {
    return originalValue ? 'TRUE()' : 'FALSE()';
  }

  if (type === 'number') {
    return originalValue;
  }

  if (originalValue === null) {
    return originalValue;
  }

  return originalValue.replaceAll('"', '\\"').replaceAll("'", "\\'").replaceAll('\n', '\\n ');
}

export function getFormulaRelevantIds(equationFormula: string) {
  const regex = /\[\/\/(.*?)]/g;
  const ids = [];

  let match;
  while ((match = regex.exec(equationFormula)) !== null) {
    const [id] = match[1].split(' | ');
    ids.push(id);
  }

  return ids;
}

export function evaluateFormula(formula: string, values: { id: string; value: any; control: AbstractControl<any> }[]) {
  const regex = /\[\/\/(.*?)]/g;
  let match;

  let formulaClone = formula;

  while ((match = regex.exec(formula)) !== null) {
    const [id, value] = match[1].split(' | ');
    const originalValue = values.find((item) => item.id === id)?.value;

    // Enhanced type detection for date/time values
    const isDateValue = originalValue instanceof Date || originalValue === DEFAULT_INITIAL_DATE;
    const isTimeString = typeof originalValue === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(originalValue);
    const isDateString =
      typeof originalValue === 'string' &&
      (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(originalValue) || /^\d{4}-\d{1,2}-\d{1,2}$/.test(originalValue));

    const originalType = isDateValue || isTimeString || isDateString ? 'date' : typeof originalValue;

    const safeValue = getFormulaValueReady(originalType, originalValue);

    formulaClone = formulaClone.replaceAll('"', "'");

    const dynamicValueReadyForFormula = originalType === 'string' ? `'${safeValue}'` : safeValue;

    const isNumeric = /^\d*$/.test(value);
    const isBoolean = ['TRUE', 'FALSE'].includes(value);

    const fixedValueReadyForFormula = isNumeric || isBoolean ? String(value) : `'${value}'`;

    formulaClone = formulaClone.replaceAll(
      match[0],
      value
        ? match[1]
            .replace(id, dynamicValueReadyForFormula)
            .replace(' | ', ' = ')
            .replace(` = ${value}`, ` = ${fixedValueReadyForFormula}`)
        : dynamicValueReadyForFormula
    );

    formulaClone = formulaClone.replace(/ = /g, ' === ');
    formulaClone = formulaClone.replace(/ != /g, ' !== ');

    // console.log('FORMULA BEFORE REPLACEMENTS', formula);

    // edge case: date values can't be directly compared
    formulaClone = formulaClone.replace(
      /(\w+\([^)]*\))\s*(===|!==)\s*(\w+\([^)]*\))/g,
      (match, left, operator, right) => {
        const datePattern = /(DATEVALUE|TODAY|NOW|DATE|TIME|DATEADD|DATEDIFF)\(/;
        if (datePattern.test(left) || datePattern.test(right)) {
          return `(${left}).getTime() ${operator} (${right}).getTime()`;
        }
        return match;
      }
    );

    // console.log('FORMULA AFTER DATE REPLACEMENT', formulaClone);
  }

  try {
    if (!formulaClone) return '';

    const safeContext = {
      ...Math,
      ...formulajs,
      ...transformers,
      window: undefined,
      document: undefined,
      global: undefined,
      process: undefined,
      require: undefined,
      eval: undefined,
      Function: undefined
    };

    const result = new Function('context', `with(context) { return ${formulaClone}; }`)(safeContext);
    // console.log('SAFE FORMULA RESULT: ', formulaClone, result);

    return result;
  } catch (e) {
    // console.log('ORIGINAL FORMULA', formula, 'FORMULA TO EVAL: ', formulaClone);
    // console.error(e);
    return null;
  }
}

// LEGACY

export default function (equationFormula: string, formSignal: WritableSignal<FormUserData>) {
  const regex = /\[\/\/(.*?)]/g;
  let match;

  let formula = equationFormula;

  while ((match = regex.exec(equationFormula)) !== null) {
    const [id, value] = match[1].split(' | ');
    const originalValue = formSignal()[id]?.value;
    const stringValue = formSignal()[id]?.stringValue || '';
    const originalType =
      originalValue instanceof Date || stringValue === DEFAULT_INITIAL_DATE ? 'date' : typeof originalValue;

    const safeValue = getSafeValueByType(originalType, stringValue, originalValue);

    formula = formula.replaceAll('"', "'");

    const dynamicValueReadyForFormula = originalType === 'string' ? `'${safeValue}'` : safeValue;

    const isNumeric = /^\d*$/.test(value);
    const isBoolean = ['TRUE', 'FALSE'].includes(value);

    const fixedValueReadyForFormula = isNumeric || isBoolean ? String(value) : `'${value}'`;

    formula = formula.replaceAll(
      match[0],
      value
        ? match[1]
            .replace(id, dynamicValueReadyForFormula)
            .replace(' | ', ' = ')
            .replace(` = ${value}`, ` = ${fixedValueReadyForFormula}`)
        : dynamicValueReadyForFormula
    );

    formula = formula.replace(/ = /g, ' === ');
    formula = formula.replace(/ != /g, ' !== ');

    // console.log('FORMULA BEFORE DATE REPLACEMENT', formula);

    // edge case: date values can't be directly compared
    formula = formula.replace(/(\w+\([^)]*\))\s*(===|!==)\s*(\w+\([^)]*\))/g, (match, left, operator, right) => {
      const datePattern = /(DATEVALUE|TODAY|NOW|DATE|TIME|DATEADD|DATEDIFF)\(/;
      if (datePattern.test(left) || datePattern.test(right)) {
        return `(${left}).getTime() ${operator} (${right}).getTime()`;
      }
      return match;
    });

    // console.log('FORMULA AFTER DATE REPLACEMENT', formula);
  }

  try {
    if (!formula) return '';

    const safeContext = {
      ...Math,
      ...formulajs,
      ...transformers,
      window: undefined,
      document: undefined,
      global: undefined,
      process: undefined,
      require: undefined,
      eval: undefined,
      Function: undefined
    };

    return new Function('context', `with(context) { return ${formula}; }`)(safeContext);
  } catch (e) {
    console.log('ORIGINAL FORMULA', equationFormula, 'FORMULA TO EVAL: ', formula);
    console.error(e);
    return null;
  }
}
